import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { TablePaginationConfig } from "antd/es/table";
import { SorterResult } from "antd/es/table/interface";
import Tabs from "antd/es/tabs";
import { subDays } from "date-fns/esm";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import {
  EntitySupportingRecordRestoration,
  EntityTypesSupportingRestoration,
} from "@mapmycustomers/shared/types/entity";
import User from "@mapmycustomers/shared/types/User";
import useDebouncedCallback from "@mapmycustomers/shared/util/hook/useDebouncedCallback";
import { Button, Popconfirm, SearchField } from "@mapmycustomers/ui";

import RangePicker from "@app/component/input/RangePicker";
import Table from "@app/component/Table";
import { isCurrentUserReadOnly } from "@app/store/iam";
import { getAllUsersOfEntireOrganization } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import { DEFAULT_PAGE_SIZE } from "@app/util/consts";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName, getEntityTypeDisplayName } from "@app/util/ui";

import SettingPage from "../common/SettingPage";

import { DELETED_RECORDS_RESTORABLE_DAYS_LIMIT } from "./consts";
import messages from "./messages";
import styles from "./RestorableRecords.module.scss";
import { getDeletedEntities, getTotalDeletedEntities, isLoading } from "./store";
import { fetchDeletedEntities, restoreRecords } from "./store/actions";
import useColumns from "./useColumns";

const initialTableState = {
  fieldName: "name",
  order: SortOrder.ASC,
  page: 1,
};

interface TableState {
  fieldName: string;
  order: SortOrder;
  page: number;
}

interface Props {
  allUsers: User[];
  entities?: EntitySupportingRecordRestoration[];
  isReadOnlyMember: boolean;
  loading: boolean;
  onFetchDeletedEntities: typeof fetchDeletedEntities.request;
  onRestoreRecords: typeof restoreRecords.request;
  total: number;
}

export const RestorableRecords: React.FC<Props> = ({
  allUsers,
  entities,
  isReadOnlyMember,
  loading,
  onFetchDeletedEntities,
  onRestoreRecords,
  total,
}) => {
  const intl = useIntl();

  const analytics = useExtendedAnalytics(
    ["Settings", "Data", "Restorable Records"],
    useAnalytics()
  );

  const [search, setSearch] = useState<string>("");
  const [entityType, setEntityType] = useState<EntityTypesSupportingRestoration>(
    EntityType.COMPANY
  );
  const [tableState, setTableState] = useState<TableState>(initialTableState);

  const [selectedRecordsIds, setSelectedRecordsIds] = useState<
    EntitySupportingRecordRestoration["id"][]
  >([]);

  const handleTabClick = useCallback(
    (key) => {
      setEntityType(key);
      setSelectedRecordsIds([]);
      setSearch("");
      setTableState(initialTableState);
      analytics.clicked([getEntityTypeAnalyticsName(key, false, true)]);
    },
    [analytics]
  );

  const items = useMemo(
    () =>
      [EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL, EntityType.ACTIVITY].map(
        (entityType) => ({
          key: entityType,
          label: getEntityTypeDisplayName(intl, entityType, { lowercase: false, plural: true }),
        })
      ),
    [intl]
  );

  const columns = useColumns(selectedRecordsIds.length > 0, onRestoreRecords, allUsers);

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      _,
      sorting:
        | SorterResult<EntitySupportingRecordRestoration>
        | SorterResult<EntitySupportingRecordRestoration>[]
    ) => {
      setSelectedRecordsIds([]);
      const stateToUpdate = { ...tableState, page: pagination.current ?? 1 };

      if (!Array.isArray(sorting) && sorting.columnKey) {
        const fieldName: string | undefined = (
          sorting as SorterResult<EntitySupportingRecordRestoration>
        ).columnKey as string;
        const order = sorting.order === "descend" ? SortOrder.DESC : SortOrder.ASC;
        stateToUpdate.fieldName = fieldName;
        stateToUpdate.order = order;
        if (fieldName !== tableState.fieldName) {
          stateToUpdate.page = 1;
        }
      }
      setTableState(stateToUpdate);
    },
    [tableState]
  );

  const handleRestoreRecords = useCallback(() => {
    onRestoreRecords({ entityType, ids: selectedRecordsIds });
    setSelectedRecordsIds([]);
  }, [entityType, onRestoreRecords, selectedRecordsIds]);

  useEffect(() => {
    onFetchDeletedEntities({
      entityType,
      page: tableState.page,
      sortFieldName: tableState.fieldName,
      sortOrder: tableState.order,
    });
  }, [entityType, onFetchDeletedEntities, tableState.fieldName, tableState.order, tableState.page]);

  const handleSearch = useCallback(
    (query: string) => {
      onFetchDeletedEntities({
        entityType,
        page: tableState.page,
        query,
        sortFieldName: tableState.fieldName,
        sortOrder: tableState.order,
      });
    },
    [entityType, onFetchDeletedEntities, tableState.fieldName, tableState.order, tableState.page]
  );

  const handleChange = useDebouncedCallback(
    [handleSearch, 500, { trailing: true }],
    [handleSearch]
  );

  const onSelectChange = useCallback((newSelectedRowKeys: React.Key[]) => {
    setSelectedRecordsIds(newSelectedRowKeys as EntitySupportingRecordRestoration["id"][]);
  }, []);

  return (
    <SettingPage pageContentClassName={styles.container} title={intl.formatMessage(messages.title)}>
      {isReadOnlyMember ? (
        <div className={styles.emptyContainer}>{intl.formatMessage(messages.noAccess)}</div>
      ) : (
        <>
          <span className={styles.subtext}>
            {intl.formatMessage(messages.subtext, { count: DELETED_RECORDS_RESTORABLE_DAYS_LIMIT })}
          </span>
          {selectedRecordsIds.length > 0 && (
            <div className={styles.restore}>
              <span>
                {intl.formatMessage(messages.restoreSelected, { count: selectedRecordsIds.length })}
              </span>
              <Popconfirm
                cancelText={intl.formatMessage(messages.cancel)}
                description={intl.formatMessage(messages.confirmTitle)}
                okText={intl.formatMessage(messages.restore)}
                onConfirm={handleRestoreRecords}
              >
                <Button type="filled">{intl.formatMessage(messages.restore)}</Button>
              </Popconfirm>
            </div>
          )}
          <div className={styles.content}>
            <div className={styles.topContainer}>
              <Tabs activeKey={entityType} items={items} onTabClick={handleTabClick} />
              <div className={styles.filters}>
                <RangePicker
                  defaultValue={[
                    subDays(new Date(), DELETED_RECORDS_RESTORABLE_DAYS_LIMIT),
                    new Date(),
                  ]}
                  disabled
                />
                <SearchField
                  allowClear
                  onChange={setSearch}
                  onSearch={handleChange}
                  showEnterButton
                  value={search}
                />
              </div>
            </div>
            <Table<EntitySupportingRecordRestoration>
              className={styles.table}
              columns={columns}
              dataSource={entities ?? []}
              loading={loading}
              onChange={handleTableChange}
              pagination={{
                current: tableState.page,
                pageSize: DEFAULT_PAGE_SIZE,
                position: ["bottomCenter"],
                showSizeChanger: false,
                total,
              }}
              rowKey="id"
              rowSelection={{
                onChange: onSelectChange,
                selectedRowKeys: selectedRecordsIds,
                type: "checkbox",
              }}
              scrollable
              showBorder
            />
          </div>
        </>
      )}
    </SettingPage>
  );
};

const mapStateToProps = (state: RootState) => ({
  allUsers: getAllUsersOfEntireOrganization(state),
  entities: getDeletedEntities(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  loading: isLoading(state),
  total: getTotalDeletedEntities(state),
});

const mapDispatchToProps = {
  onFetchDeletedEntities: fetchDeletedEntities.request,
  onRestoreRecords: restoreRecords.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(RestorableRecords);
